import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { Table } from "simplygrow-ui"
import { mdiFilter, mdiFilterOutline } from "@mdi/js"

import { ProfilePk, QueryResult, ColumnConfig } from "../../../types"
import { CustomFilterState } from "../../Tables/DataTable/DataTable"
import { TableService, FilterService } from "../../../services"
import { ActionButtonType, Toolbar } from "../../GeneralUI/Toolbar"
import { ColorPalette } from "../../../config"
import Modal from "../../Modals/Modal"
import FieldFilter from "../../GeneralUI/FieldFilter/FieldFilter"
import { TABLE_ROW_HEIGHT } from "../../../constants/table"
import { cloneDeep } from "lodash"

type DataQueryTrendsProfileProps = {
  selectedProfileIds: ProfilePk[]
  profileFilterConfig: { key: string; label: string }[]
  profileTableColumnConfig: ColumnConfig[]
  profileTableRowData: QueryResult[]
  onBackClick: () => void
  onProfileSelect: (profileIds: ProfilePk[]) => void
}

const tableService = new TableService()
const filterService = new FilterService()

export const DataQueryTrendsProfiles = (props: DataQueryTrendsProfileProps) => {
  const tableRef = useRef<HTMLDivElement>(null)

  const [tableData, setTableData] = useState<Record<string, string>[]>([])
  const [refreshTimestamp, setRefreshTimestamp] = useState<number>(Date.now())
  const [tableHeight, setTableHeight] = useState<number>(500)
  const [filterModalOpen, setFilterModalOpen] = useState(false)
  const [filters, setFilters] = useState<CustomFilterState[]>([])

  useEffect(() => {
    updateTableData(filters)
  }, [props.profileTableRowData, props.profileTableColumnConfig])

  useLayoutEffect(() => {
    if (tableRef.current) {
      const tableTopY = tableRef.current.getBoundingClientRect().top
      const browserWindowHeight = window.innerHeight * 0.85
      setTableHeight(browserWindowHeight - tableTopY)
    }
  }, [tableRef.current])

  const updateTableData = (filtersToApply: CustomFilterState[]) => {
    const generateRowData = (result: QueryResult) => {
      let rowData = { key: result.profilePk } as Record<string, string>
      props.profileTableColumnConfig.forEach((column) => (rowData[column.key] = result[column.key] ?? "-"))
      return rowData
    }

    let filteredData = cloneDeep(props.profileTableRowData.map(generateRowData))
    if (filtersToApply.length) {
      filteredData = filterService.applyCustomFilters(filteredData, filtersToApply)
    }
    setTableData(filteredData)
    setRefreshTimestamp(Date.now())
  }

  const handleRowSelect = (selectedProfilePks: ProfilePk[]) => {
    if (selectedProfilePks.length > 0) {
      props.onProfileSelect(selectedProfilePks)
    }
  }

  const toolbarActionButtons: ActionButtonType[] = [
    {
      iconPath: filters.length > 0 ? mdiFilter : mdiFilterOutline,
      iconColor: ColorPalette.PRIMARY_BLUE,
      onClick: () => setFilterModalOpen(true),
      label: "FILTER",
      title: "Set custom filters on table data",
      count: filters.length,
    },
  ]

  const columnConfig = tableService.convertPfColumnConfigToGridColDef(props.profileTableColumnConfig)
  return (
    <div style={{ ...styles.container, height: "85%" }}>
      <Toolbar actionButtons={toolbarActionButtons} key={`actionToolbar_${toolbarActionButtons.length}`} />
      <div ref={tableRef} style={{ width: "100%" }}>
        <Table
          rows={tableData}
          columns={columnConfig}
          tableHeight={tableHeight}
          rowHeight={TABLE_ROW_HEIGHT}
          rowSelection={true}
          selectedRowIds={props.selectedProfileIds}
          onRowSelection={handleRowSelect}
          key={`dataTable_${refreshTimestamp}`}
        />
      </div>

      <Modal open={filterModalOpen} title="" contentContainerStyle={{ padding: 0 }}>
        <FieldFilter
          filtersOnLoad={filters}
          filterConfig={props.profileFilterConfig}
          onDoneClick={(updatedFilters) => {
            setFilters(updatedFilters)
            updateTableData(updatedFilters)
            setFilterModalOpen(false)
          }}
        />
      </Modal>
    </div>
  )
}

const styles = {
  container: {
    display: "flex",
    margin: "10px 10px 15px",
    flexDirection: "column" as "column",
  },
}
